import type {
  DateFieldType,
  DateInputProperties,
  FormElement,
  HeaderProperties,
  TableCell,
  TableProperties,
} from '../model/form.types';
import { DATE_FIELD_TYPE_VALUES, DEFAULT_DATE_FIELD_TYPES } from '../model/date-field-types';
import { DEFAULT_HEADER_MAIN_LABEL, DEFAULT_HEADER_MARGIN } from '../model/header-defaults';
import { decimalFromStored } from './decimal';

type ElementMigration = (element: FormElement) => FormElement;

function migrateTableCells(element: FormElement, migrateElement: ElementMigration): FormElement {
  if (element.type !== 'TABLE') return element;

  const tableProps = element.properties as TableProperties;
  if (!tableProps.cells) return element;

  const migratedCells = tableProps.cells.map((row: TableCell[]) =>
    row.map((cell: TableCell) => {
      if (!cell.content) return cell;
      return { ...cell, content: migrateElement(cell.content) };
    }),
  );

  return {
    ...element,
    properties: { ...tableProps, cells: migratedCells } as FormElement['properties'],
  };
}

function migrateElementTree(elements: FormElement[], migrateElement: ElementMigration): FormElement[] {
  return elements.map((element) => migrateTableCells(migrateElement(element), migrateElement));
}

function migrateNumberInputElement(element: FormElement): FormElement {
  if (element.type !== 'NUMBER_INPUT') return element;

  const props = { ...element.properties };
  const anyProps = props as Record<string, unknown>;

  if (anyProps.min !== undefined && anyProps.minValue === undefined) {
    anyProps.minValue = anyProps.min;
  }
  if (anyProps.max !== undefined && anyProps.maxValue === undefined) {
    anyProps.maxValue = anyProps.max;
  }

  delete anyProps.min;
  delete anyProps.max;
  delete anyProps.step;
  delete anyProps.locale;
  delete anyProps.fraction;
  delete anyProps.unsigned;

  // Limits saved as numbers, and a "Valor" typed with ',' as the decimal mark, become the canonical
  // decimal strings the engine compares; a "Valor" that reads as no number is kept for the author.
  for (const key of ['minValue', 'maxValue'] as const) {
    if (anyProps[key] === undefined) continue;
    const decimal = decimalFromStored(anyProps[key]);
    if (decimal === null) delete anyProps[key];
    else anyProps[key] = decimal;
  }
  if (typeof anyProps.value === 'string' && anyProps.value.trim().length > 0) {
    anyProps.value = decimalFromStored(anyProps.value) ?? anyProps.value;
  }

  return { ...element, properties: props };
}

export function migrateNumberInputProperties(elements: FormElement[]): FormElement[] {
  return migrateElementTree(elements, migrateNumberInputElement);
}

function isDateFieldType(value: unknown): value is DateFieldType {
  return DATE_FIELD_TYPE_VALUES.includes(value as DateFieldType);
}

function migrateDateInputElement(element: FormElement): FormElement {
  if (element.type !== 'DATE_INPUT') return element;

  const { fieldType, ...rest } = element.properties as DateInputProperties & {
    fieldType?: unknown;
  };
  if (rest.fieldTypes !== undefined && fieldType === undefined) return element;

  const fieldTypes =
    rest.fieldTypes ?? (isDateFieldType(fieldType) ? [fieldType] : [...DEFAULT_DATE_FIELD_TYPES]);

  const properties: DateInputProperties = { ...rest, fieldTypes };
  return { ...element, properties };
}

export function migrateDateInputProperties(elements: FormElement[]): FormElement[] {
  return migrateElementTree(elements, migrateDateInputElement);
}

function shortIdFromTableId(tableId: string): string {
  const match = tableId.match(/header-table-(.+)$/);
  return match ? match[1] : tableId.slice(-8);
}

function extractTextInputField(
  name: string,
  value: string,
  fields: { rq: string; rev: string; title: string },
): void {
  if (name.includes('rq')) {
    fields.rq = value;
  } else if (name.includes('rev')) {
    fields.rev = value;
  } else if (name.includes('titulo') || name.includes('title')) {
    fields.title = value;
  }
}

function extractHeaderFields(cells: TableCell[][]): { mainLabel: string; rq: string; rev: string; title: string } {
  const fields = { mainLabel: DEFAULT_HEADER_MAIN_LABEL, rq: '', rev: '', title: '' };
  for (const row of cells) {
    for (const cell of row) {
      const content = cell?.content;
      if (!content) continue;
      if (content.type === 'FIXED_TEXT' && content.properties?.content != null) {
        fields.mainLabel = String(content.properties.content);
      } else if (content.type === 'TEXT_INPUT') {
        const name = (content.properties?.name ?? '') as string;
        const value = (content.properties?.value ?? content.properties?.placeholder ?? '') as string;
        extractTextInputField(name, value, fields);
      }
    }
  }
  return fields;
}

function buildHeaderFromTable(el: FormElement, tableProps: TableProperties): FormElement {
  const tableMargin = tableProps.margin ?? DEFAULT_HEADER_MARGIN;
  const { mainLabel, rq, rev, title } = extractHeaderFields(tableProps.cells ?? []);
  const shortId = shortIdFromTableId(el.id);
  return {
    id: `header-${shortId}`,
    type: 'HEADER',
    position: el.position,
    properties: {
      name: `header_${shortId}`,
      mainLabel,
      rq,
      rev,
      title,
      marginByPage: { 0: { ...tableMargin } },
    } as HeaderProperties,
  };
}

export function migrateTableHeaderToHeader(elements: FormElement[]): FormElement[] {
  return elements.map((el) => {
    if (el.type !== 'TABLE') return el;
    const tableProps = el.properties as TableProperties;
    if (tableProps.isHeader !== true) return el;
    return buildHeaderFromTable(el, tableProps);
  });
}

export function migrateLegacyElements(elements: FormElement[]): FormElement[] {
  return migrateDateInputProperties(migrateNumberInputProperties(migrateTableHeaderToHeader(elements)));
}
